/**
 * B549 leg (2) — the buyer-surface group interrupt (444:2) and inquiry
 * form (446:2), pure derivations. The frames are the spec: at the
 * channel's group threshold the booking sheet's calendar column becomes
 * the GROUP BOOKING panel, and REQUEST A GROUP QUOTE walks into the
 * inquiry form (`group.inquiry`). The threshold is the mint's wire fact
 * (session.guest, leg 1); everything here derives from it and the card.
 *
 * Copy rules, each an honest derivation:
 *  - the title speaks the tenant's OWN threshold, never a hardcoded 20;
 *  - the keep-door names threshold − 1 — the largest party self-service
 *    still sells (the cart wall refuses ≥ threshold, leg 1);
 *  - the amber note tells the truth about that wall rather than promising
 *    a public-price checkout the platform would refuse.
 */
import { GROUP_INQUIRY_KINDS } from "@transact/contracts";
import type { ProductSummary } from "../ui/screens.js";
import type { Composition } from "./composition.js";
import { maxQuantity, orderCap, quantityOf, totalQuantity } from "./composition.js";
import { ApiError, ApiRequestError } from "./api.js";
import { PAGE_MISCONFIGURED, UNKNOWN_FAILURE, translateRefusal } from "./refusals.js";

export type GroupInquiryKind = (typeof GROUP_INQUIRY_KINDS)[number];

/** The interrupt replaces the calendar column at threshold, not above it. */
export function groupInterruptActive(threshold: number | undefined, ticketCount: number): boolean {
  return threshold !== undefined && threshold > 0 && ticketCount >= threshold;
}

/** The largest self-service party: the keep-door's number and the stepper's honest cap. */
export function keepCount(threshold: number): number {
  return Math.max(1, threshold - 1);
}

/**
 * The stepper's cap for one type: room under the threshold plus what this
 * type already holds — the party can REACH the threshold (the frame shows
 * 20, the panel appears) but never pass it.
 */
export function cappedMax(baseMax: number, threshold: number | undefined, ticketCount: number, own: number): number {
  if (threshold === undefined || threshold <= 0) return baseMax;
  return Math.min(baseMax, own + Math.max(0, threshold - ticketCount));
}

/**
 * B580 — the stepper's max for one type: `orderCap` (one past a type's own
 * cap while groups are on, so that press opens the desk) under B549's
 * threshold room.
 */
export function stepperMax(card: ProductSummary, variantId: string, threshold: number | undefined, ticketCount: number, own: number): number {
  return cappedMax(orderCap(card, variantId, threshold), threshold, ticketCount, own);
}

/** B580 — the type whose own cap a party passed: its cap and its name. */
export interface CapMet {
  readonly count: number;
  readonly name: string;
}

/**
 * B549 + B580 — is this party a group, and at what size? The threshold
 * rules when the party reaches it; otherwise a type held one past its own
 * "Most per order" (only reachable while groups are on — `orderCap`) makes
 * the party a group of that cap + 1. Undefined = not a group: buy online.
 */
export function groupParty(card: ProductSummary, comp: Composition, threshold: number | undefined): { readonly size: number; readonly cap?: CapMet } | undefined {
  if (threshold === undefined || threshold <= 0) return undefined;
  if (groupInterruptActive(threshold, totalQuantity(comp))) return { size: threshold };
  let met: CapMet | undefined;
  for (const v of card.variants) {
    const cap = v.constraints?.maxPerOrder;
    if (cap === undefined || quantityOf(comp, v.variantId) <= cap) continue;
    if (met === undefined || cap < met.count) met = { count: cap, name: v.name };
  }
  return met === undefined ? undefined : { size: met.count + 1, cap: met };
}

/**
 * "or keep 19 tickets" — step the party back under the threshold: largest
 * lines give first, deterministically, until the total is threshold − 1.
 * B580: given the card, a type held past its own cap first steps back to
 * that cap (what online checkout sells).
 */
export function reduceToKeep(comp: Composition, threshold: number, card?: ProductSummary): Composition {
  if (card !== undefined) {
    const capped: Record<string, number> = { ...comp };
    for (const v of card.variants) {
      if (capped[v.variantId] !== undefined) capped[v.variantId] = Math.min(capped[v.variantId]!, maxQuantity(card, v.variantId));
    }
    comp = capped;
  }
  const target = keepCount(threshold);
  let excess = totalQuantity(comp) - target;
  if (excess <= 0) return comp;
  const entries = Object.entries(comp)
    .filter(([, q]) => q > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const next: Record<string, number> = { ...comp };
  for (const [variantId, q] of entries) {
    if (excess <= 0) break;
    const give = Math.min(q, excess);
    next[variantId] = q - give;
    excess -= give;
  }
  return next;
}

/* ------------------------------------------------------------ the copy */

/** B549 (9) — who replies: the desk's own first name when the channel names one, else the desk. */
export function deskRepliesLead(deskName: string | undefined): string {
  return deskName === undefined ? "The groups desk replies" : `${deskName} at the groups desk replies`;
}

/** The inquiry's one-line promise (the sheet foot, the sticky bar's lead). */
export function deskRepliesWords(deskName: string | undefined): string {
  return `${deskRepliesLead(deskName)} within one business day with a quote`;
}

/** Step 1 of WHAT HAPPENS NEXT (446:2: "Maya sends a quote"). */
export function deskSendsWords(deskName: string | undefined): string {
  return deskName === undefined ? "The desk sends a quote" : `${deskName} sends a quote`;
}

export function groupPanelTitle(threshold: number): string {
  return `${threshold} or more? That’s a group.`;
}

export function groupPanelBlurb(threshold: number, businessName: string | undefined): string {
  const who = businessName ?? "This venue";
  return `${who} has a groups desk for schools, companies and tours — group rates, one invoice, and a coordinator who plans the visit with you. Tell us about your party of ${threshold}+ and a quote comes back, usually the same day.`;
}

export function groupNoteText(threshold: number, cap?: CapMet): string {
  if (cap !== undefined) {
    /* B580 — the wall the party met is the TYPE's cap, not the threshold: say that one. */
    return `Prefer to buy ${threshold} tickets right now at the public price? Online checkout sells at most ${cap.count} ${cap.name} tickets per order — keep ${cap.count} below, or send the inquiry and let the desk price all ${threshold}.`;
  }
  return `Prefer to buy ${threshold} tickets right now at the public price? Online checkout stops at ${keepCount(threshold)} so parties of ${threshold} or more always get the group rate — keep ${keepCount(threshold)} below, or send the inquiry and let the desk price all ${threshold}.`;
}

/** "20 adults · above the group threshold — group pricing applies" (444:2). */
export function groupFooterLine(party: string | undefined, ticketCount: number): string {
  return `${party ?? `${ticketCount} tickets`} · above the group threshold — group pricing applies`;
}

export function keepTicketsLabel(threshold: number): string {
  return keepLabel(keepCount(threshold));
}

/** B580 — the keep door names what the party keeps (`reduceToKeep`'s total). */
export function keepLabel(count: number): string {
  return `or keep ${count} tickets and continue ›`;
}

/** B580 — the + at a type's cap, while groups are on, is the groups door; its name says so. */
export function capDoorLabel(cap: number): string {
  return `More than ${cap}? Book as a group`;
}

/** B580 — the row's own sentence when it stands at its cap with groups on. */
export function capRowHint(cap: number, name: string): string {
  return `${cap} is the most ${name} tickets one order takes online — press + to book a bigger party as a group.`;
}

/** "20+ guests · public $32.00" — the group rate itself is the desk's to quote. */
export function groupExperienceLine(threshold: number, publicFrom: string | undefined): string {
  return publicFrom === undefined ? `${threshold}+ guests` : `${threshold}+ guests · public ${publicFrom}`;
}

/* ------------------------------------------------------- the form facts */

export interface GroupInquiryDraft {
  readonly organisation: string;
  readonly headcount: string;
  readonly kind: GroupInquiryKind;
  readonly contactName: string;
  readonly contactEmail: string;
  readonly contactPhone: string;
  readonly preferredDate: string;
  readonly notes: string;
}

/** The form opens knowing what the party already said: the count and the day. */
export function seedInquiryDraft(ticketCount: number, preferredDate: string | undefined): GroupInquiryDraft {
  return {
    organisation: "",
    headcount: ticketCount > 0 ? String(ticketCount) : "",
    kind: "school",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    preferredDate: preferredDate ?? "",
    notes: "",
  };
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

/** What still gates SEND, in buyer words — empty means the door is armed. */
export function inquiryGaps(d: GroupInquiryDraft): readonly string[] {
  const gaps: string[] = [];
  if (d.organisation.trim() === "") gaps.push("who the group is");
  const count = Number(d.headcount);
  if (!Number.isSafeInteger(count) || count < 1) gaps.push("how many are coming");
  if (d.contactName.trim() === "") gaps.push("your name");
  if (!EMAIL_SHAPE.test(d.contactEmail.trim())) gaps.push("an email we can reply to");
  return gaps;
}

/**
 * B589 (FORMS.md R19) — what is wrong with each TYPED value, in the field's
 * own words, by the contract's own bounds (`group.inquiry`: organization
 * ≤200, name ≤120, email ≤254, phone 3–40, headcount 1–100,000, notes
 * ≤2000). Nothing typed is not an error — the shut door says what to add.
 * Before this a phone or a note the contract refuses ARMED the door, and
 * the page's own schema check threw before the wire: "Something went wrong".
 */
export type InquiryField = "organisation" | "headcount" | "contactName" | "contactEmail" | "contactPhone" | "notes";

const TOO_LONG = (limit: string): string => `That’s longer than the desk’s form takes — ${limit} characters at most.`;

export function inquiryFieldErrors(d: GroupInquiryDraft): Partial<Record<InquiryField, string>> {
  const errors: Partial<Record<InquiryField, string>> = {};
  if (d.organisation.trim().length > 200) errors.organisation = TOO_LONG("200");
  const count = d.headcount.trim();
  if (count !== "") {
    const n = Number(count);
    if (!/^\d+$/.test(count) || !Number.isSafeInteger(n)) errors.headcount = "Type how many are coming as a whole number, like 25.";
    else if (n < 1) errors.headcount = "A group needs at least 1 person coming.";
    else if (n > 100_000) errors.headcount = "That’s more than one inquiry carries — 100,000 at most. Say the rest in your note.";
  }
  if (d.contactName.trim().length > 120) errors.contactName = TOO_LONG("120");
  const email = d.contactEmail.trim();
  if (email !== "") {
    if (!EMAIL_SHAPE.test(email)) errors.contactEmail = "That isn’t a full email address yet — it needs a name, an @ and a domain, like dana@school.org.";
    else if (email.length > 254) errors.contactEmail = TOO_LONG("254");
  }
  const phone = d.contactPhone.trim();
  if (phone !== "" && phone.length < 3) errors.contactPhone = "A phone number needs at least 3 characters — or leave it empty, it’s optional.";
  else if (phone.length > 40) errors.contactPhone = TOO_LONG("40");
  if (d.notes.trim().length > 2000) errors.notes = TOO_LONG("2,000");
  return errors;
}

/** The fields' own names in the gate sentence ("fix the phone number above"). */
const FIELD_WORDS: Readonly<Record<InquiryField, string>> = {
  organisation: "the organization",
  headcount: "how many",
  contactName: "your name",
  contactEmail: "the email",
  contactPhone: "the phone number",
  notes: "your note",
};

/** R14 — ONE guard for SEND's click and its Enter: every fact present, every typed value usable. */
export function inquiryReady(d: GroupInquiryDraft): boolean {
  return inquiryGaps(d).length === 0 && Object.keys(inquiryFieldErrors(d)).length === 0;
}

/**
 * The shut door's reason (R15), or undefined when armed. A required fact
 * whose typed value is wrong is already named at its field, so it is a
 * FIX, not an ADD — the sentence never asks for what the buyer has typed.
 */
export function inquiryGateReason(d: GroupInquiryDraft): string | undefined {
  const errors = inquiryFieldErrors(d);
  const typedWrong = new Set<string>();
  if (errors.headcount !== undefined) typedWrong.add("how many are coming");
  if (errors.contactEmail !== undefined) typedWrong.add("an email we can reply to");
  const add = inquiryGaps(d).filter((g) => !typedWrong.has(g));
  const fix = (Object.keys(FIELD_WORDS) as InquiryField[]).filter((f) => errors[f] !== undefined).map((f) => FIELD_WORDS[f]);
  const list = (xs: readonly string[]): string => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);
  if (add.length === 0 && fix.length === 0) return undefined;
  if (fix.length === 0) return `Add ${add.join(", ")} to send.`;
  if (add.length === 0) return `Fix ${list(fix)} above to send.`;
  return `Add ${add.join(", ")}, and fix ${list(fix)} above, to send.`;
}

/**
 * B589 (R18) — a refused send, split by where the buyer can act on it: the
 * page's own schema refusal naming a field the buyer typed pins at THAT
 * field; one naming no visible field is the page's fault (PAGE_MISCONFIGURED
 * — a retry cannot cure it); a wire refusal keeps its buyer sentence + Ref.
 */
export interface InquiryRefusal {
  readonly text?: string | undefined;
  readonly reference?: string | undefined;
  readonly fields: Partial<Record<InquiryField, string>>;
}

export const REFUSED_AT_FIELD = "The booking service didn’t accept this — check it and send again.";

export function inquirySendRefusal(error: unknown): InquiryRefusal {
  if (error instanceof ApiRequestError) {
    const fields: Partial<Record<InquiryField, string>> = {};
    for (const path of error.fields) {
      const f = path.split(".")[0] as InquiryField;
      if (f in FIELD_WORDS) fields[f] = REFUSED_AT_FIELD;
    }
    return Object.keys(fields).length > 0 ? { fields } : { text: PAGE_MISCONFIGURED, fields };
  }
  if (error instanceof ApiError) return { text: translateRefusal(error), reference: error.requestId, fields: {} };
  return { text: UNKNOWN_FAILURE, fields: {} };
}

/** The wire body, built only when the gaps are empty (the caller checks). */
export function inquiryBody(d: GroupInquiryDraft, channelId: string): Record<string, unknown> {
  return {
    channelId,
    organisation: d.organisation.trim(),
    contactName: d.contactName.trim(),
    contactEmail: d.contactEmail.trim(),
    ...(d.contactPhone.trim() !== "" ? { contactPhone: d.contactPhone.trim() } : {}),
    ...(DATE_SHAPE.test(d.preferredDate) ? { preferredDate: d.preferredDate } : {}),
    headcount: Number(d.headcount),
    kind: d.kind,
    ...(d.notes.trim() !== "" ? { notes: d.notes.trim() } : {}),
  };
}
